import { createRenderer } from './render/createRenderer';
import { DEFAULT_PALETTE, pickNextPalette, applyPaletteToDocument } from './palette/palettes';
import { SceneController } from './app/SceneController';
import { isDebugEnabled, mountDebugPanel } from './ui/debugPanel';
import { initCustomCursor } from './ui/customCursor';

async function boot(): Promise<void> {
  initCustomCursor();
  const canvas = document.querySelector<HTMLCanvasElement>('#scene-canvas');
  if (!canvas) throw new Error('Missing #scene-canvas');

  const palette = DEFAULT_PALETTE;
  applyPaletteToDocument(palette);

  const renderer = await createRenderer(canvas);
  const { app } = renderer;
  const controller = new SceneController(app, palette);

  const resize = (): void => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    app.renderer.resize(w, h);
    controller.resize(w, h);
  };
  resize();
  window.addEventListener('resize', resize);
  window.addEventListener('orientationchange', resize);

  const skip = document.querySelector<HTMLAnchorElement>('.skip-contact');
  skip?.addEventListener('click', (e) => {
    e.preventDefault();
    controller.skipToContact();
    document.getElementById('contact-name')?.focus();
  });

  const debug = isDebugEnabled();
  let disposeDebug: (() => void) | null = null;
  if (debug) {
    const panel = document.getElementById('debug-panel');
    if (panel) {
      let frames = 0;
      let last = performance.now();
      let fps = 60;
      disposeDebug = mountDebugPanel(
        panel,
        controller.tuning,
        () => {
          // live tuning object is shared with AlignScene
        },
        () => {
          frames += 1;
          const now = performance.now();
          if (now - last >= 500) {
            fps = (frames * 1000) / (now - last);
            frames = 0;
            last = now;
          }
          const align = controller.alignScene?.debugStats ?? null;
          return {
            fps,
            steps: align?.steps ?? 0,
            align,
            paletteId: controller.paletteId,
          };
        },
        () => controller.resetAlign(),
        () => {
          const next = pickNextPalette(controller.paletteId);
          applyPaletteToDocument(next);
          controller.applyPaletteAndReset(next);
        },
      );
    }

    window.addEventListener('keydown', (e) => {
      if (e.key === 'r' || e.key === 'R') {
        if ((e.target as HTMLElement)?.closest?.('input, textarea, button')) return;
        controller.resetAlign();
      }
    });
  }

  let last = performance.now();
  app.ticker.add(() => {
    const now = performance.now();
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    controller.update(dt);
  });

  window.addEventListener('beforeunload', () => {
    disposeDebug?.();
    controller.destroy();
    renderer.destroy();
  });
}

boot().catch((err) => {
  console.error(err);
  document.body.innerHTML = `<p style="padding:2rem;font-family:sans-serif">Failed to start Steele.io. <a href="/archive/index.html">Open archive</a></p>`;
});
